import React, { useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router';
import { Avatar, Box, Container, Divider, List, ListItem, ListItemAvatar, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { DateFormats, providerChartsReviewRoute, collaborationProviderProfileRoute, ROUTING_CONFIG } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { Button, ProviderLayout, ProviderLayoutPageHeader, StackableCard, StackableCardDeck } from '@uplift/ui-v2';

import { ALL_STATES, SuperviseesFilters } from './components/SuperviseesFilters';
import { useAllCollaborativeChartsReviewsQuery, useAllCollaborativeProvidersDetailsQuery, useCollaboratePhysicianMatchByIdForPhysicianQuery, useUserAvatarAssetsQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { CollaborativeChartsReview, CollaborativeProviderDetails } from '@uplift/api';
import { StateLabel } from '../../components/StatesLabel';
import { getAvatarColor } from '@uplift/utils';
import { ErrorBoundary } from 'react-error-boundary';
import { CalendarDateService } from '@uplift/services';
import { DescriptionOutlined } from '@mui/icons-material';


const ChartsReviewListItem: React.VFC<{ chartsReview: CollaborativeChartsReview }> = ({ chartsReview }) => {
  const me = useCurrentuser();
  const history = useHistory();
  const { data: match } = useCollaboratePhysicianMatchByIdForPhysicianQuery(me.id, chartsReview.matchId);
  const { data: providerName } = useUserDisplayNameQuery(match.providerId, { suspense: true });
  const expireAt = CalendarDateService.date(chartsReview.expireAt, DateFormats.ISO_DATE);
  const expired = expireAt.isBefore(CalendarDateService.date());

  return (
    <ListItem
      sx={{ bgcolor: '#fff', p: 3 }}
      secondaryAction={
        <Button sx={{ minWidth: 180 }} variant="contained" color="primary" onClick={() => history.push(providerChartsReviewRoute.createLink({ reviewId: chartsReview.id }))}>Review Charts</Button>
      }
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.background', height: '64px', width: '64px' }}><DescriptionOutlined color="secondary" /></Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ ml: 2 }}
        slotProps={{
          primary: {
            fontWeight: 600
          },
          secondary: {
            mt: 0.5
          }
        }}
        primary={`${CalendarDateService.date(chartsReview.period.start, DateFormats.ISO_DATE).format('MMMM D')} Chart Review for ${providerName.full}`}
        secondary={`${expired ? 'Overdue' : 'Due'} ${expireAt.fromNow()}`}
      />
    </ListItem>
  );
};


export const ChartsReviewsSection: React.VFC = () => {
  const { data: pendingChartsReviews } = useAllCollaborativeChartsReviewsQuery({ submitted: false });

  if (!pendingChartsReviews?.length) {
    return null;
  }

  return (
    <Box>
      <Container sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="h6">Tasks</Typography>
          <Paper sx={{ mt: 2, overflow: 'hidden' }}>
            <List disablePadding>
              {pendingChartsReviews.map((cr, index) => (
                <React.Fragment key={cr.id}>
                  {index === 0 ? null : <Divider />}
                  <ChartsReviewListItem
                    chartsReview={cr}
                  />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};


const SuperviseeDetails: React.VFC<{
  supervisee: CollaborativeProviderDetails;
}> = (props) => {
  const { data: displayName } = useUserDisplayNameQuery(props.supervisee.user.id, { suspense: true });
  const { data: avatarAssets } = useUserAvatarAssetsQuery(props.supervisee.user.id, { suspense: true });

  return (
    <Box>
      <Stack direction="row" gap={3}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            color: 'white',
            bgcolor: getAvatarColor(displayName.first),
          }}
          src={avatarAssets.avatarAsset?.url}
        />
        <Stack justifyContent="center">
          <Typography fontSize={20}>{displayName.fullWithAffixes}</Typography>
          <Typography variant="body2">
            {props.supervisee.collaborativeStateIds.map((stateId, index) => (
              <React.Fragment key={stateId}>
                {index === 0 ? '' : ', '}
                <StateLabel stateId={stateId} />
              </React.Fragment>
            ))}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};


export const SuperviseesPageContent: React.VFC = () => {
  const l10n = useL10n();
  const history = useHistory();
  const me = useCurrentuser();
  const { data: collaborativeProvidersDetails = [] } = useAllCollaborativeProvidersDetailsQuery(me.id);
  const [ searchText, setSearchText ] = useState('');
  const [ selectedStateId, setSelectedStateId ] = useState<string>(ALL_STATES);
  const availableStateIds = React.useMemo(() => {
    return Array.from(new Set(collaborativeProvidersDetails.flatMap(s => s.collaborativeStateIds)));
  }, [ collaborativeProvidersDetails ]);
  const filteredCollaborativeProvidersDetails = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return collaborativeProvidersDetails
      .filter(supervisee =>
        selectedStateId === ALL_STATES
        || supervisee.licenseStateIds.includes(selectedStateId)
      )
      .filter(supervisee => `${supervisee.user.firstName} ${supervisee.user.lastName}`.toLowerCase().includes(search));
  }, [ collaborativeProvidersDetails, searchText, selectedStateId ]);

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h6">Providers</Typography>

      {collaborativeProvidersDetails.length === 0 ? (
        <Typography variant="h5" color="text.secondary" textAlign="center" sx={{ p: 3 }}>
          {l10n.supervisees.emptyState}
        </Typography>
      ) : (
        <>
          <Box sx={{ mt: 2 }}>
            <SuperviseesFilters
              searchText={searchText}
              onSearchTextChange={setSearchText}
              selectedStateId={selectedStateId}
              onSelectedStateIdChange={setSelectedStateId}
              availableStateIds={availableStateIds}
            />
          </Box>
          <StackableCardDeck>
            {filteredCollaborativeProvidersDetails.map(supervisee => (
              <StackableCard
                key={supervisee.id}
                sx={{ cursor: 'pointer' }}
                onClick={() => history.push(collaborationProviderProfileRoute.createLink({ superviseeId: supervisee.id }))}
              >
                <SuperviseeDetails supervisee={supervisee} />
              </StackableCard>
            ))}
          </StackableCardDeck>
        </>
      )}
    </Container>
  );
};

export const SuperviseesPage: React.VFC = () => {
  const l10n = useL10n();
  const me = useCurrentuser();

  if (!me.isCollaborativePhysician) {
    return <Redirect to={ROUTING_CONFIG.main} />;
  }

  return (
    <ProviderLayout>
      <ProviderLayoutPageHeader title={l10n.pages.supervisees} />
      <ErrorBoundary
        fallback={<Redirect to={ROUTING_CONFIG.collaborationManage} />}
      >
        <ChartsReviewsSection />
      </ErrorBoundary>
      <SuperviseesPageContent />
    </ProviderLayout>
  );
};
